import type { SendEventParams } from '@/utils/sendEvent';
import { PixelResponse } from '../../types';
import { BaseTracker } from './BaseTracker';
import { getCoreVisitorId } from '@/core/tracker';

export interface IAddPaymentInfoCustomer {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

export interface IAddPaymentInfoData {
  value?: number;
  currency?: string;
  paymentMethod?: string;
  items?: Array<{
    item_id: string;
    item_name: string;
    category?: string;
    quantity: number;
    price: number;
  }>;
  customer?: IAddPaymentInfoCustomer;
  metadata?: Record<string, unknown>;
}

/**
 * Tracker para eventos de adição de informação de pagamento
 */
export class AddPaymentInfoTracker extends BaseTracker {
  /**
   * Retorna o payload para envio centralizado no ConversionEngine.
   */
  public getPayload({
    value = 0,
    currency = 'BRL',
    paymentMethod,
    items,
    customer,
    metadata,
  }: IAddPaymentInfoData): SendEventParams {
    return {
      category: 'ECOMMERCE',
      type: 'ADD_PAYMENT_INFO',
      eventData: {
        value,
        currency,
        paymentMethod,
        items,
        ...(customer ? { customer } : {}),
        ...(metadata ? { metadata } : {}),
      },
      config: this.config,
      sessionId: this.sessionId,
      visitorId: this.visitorId,
      version: this.version,
    };
  }

  /**
   * Rastreia adição de informação de pagamento
   */
  public async track({
    value = 0,
    currency = 'BRL',
    paymentMethod,
    items,
    customer,
    metadata,
  }: IAddPaymentInfoData): Promise<PixelResponse> {
    return this.sendEvent(
      this.getPayload({
        value,
        currency,
        paymentMethod,
        items,
        customer,
        metadata,
      })
    );
  }
}

// Função de conveniência para compatibilidade com código existente
export const addPaymentInfo = ({
  value = 0,
  currency = 'BRL',
  paymentMethod,
  items,
  metadata,
}: IAddPaymentInfoData): Omit<
  SendEventParams,
  'config' | 'sessionId' | 'version'
> => {
  const visitorId = getCoreVisitorId();
  if (!visitorId) {
    throw new Error('Visitor ID não encontrado');
  }
  return {
    visitorId: visitorId,
    category: 'ECOMMERCE',
    type: 'ADD_PAYMENT_INFO',
    eventData: {
      value,
      currency,
      paymentMethod,
      items: items ?? [],
      ...(metadata ? { metadata } : {}),
    },
  };
};
